import { useState } from 'react';
import { StyleSheet, View } from 'react-native';

import {
  IconButton,
  IconClose,
  IconErrorFill,
  IconInfoFill,
  IconWarningFill,
  Text,
  XStack,
  YStack,
  tokens,
} from '@fhs/ui';

interface EnhancedInlineAlertProps {
  title?: string;
  status: 'error' | 'warning' | 'info';
  message: string | undefined;
  isDismissable?: boolean;
  action?: React.ReactNode;
  style?: any;
  testID?: string;
}

export function EnhancedInlineAlert({
  title,
  status,
  message,
  isDismissable = false,
  action,
  style,
  testID,
}: EnhancedInlineAlertProps) {
  const [isVisible, setIsVisible] = useState(true);

  if (!isVisible) {
    return null;
  }

  const backgroundColorByStatus = {
    info: tokens.colors.$houseLight,
    warning: tokens.colors.$warningBackground,
    error: tokens.colors.$errorBackground,
  };

  const iconByStatus = {
    info: <IconInfoFill size={20} color={tokens.colors.$houseDark} />,
    warning: <IconWarningFill size={20} color={tokens.colors.$warning} />,
    error: <IconErrorFill size={20} color={tokens.colors.$error} />,
  };

  const backgroundColor = backgroundColorByStatus[status];
  const icon = iconByStatus[status];

  return (
    <View testID={testID} style={[styles.container, { backgroundColor }, style]}>
      <XStack style={styles.alertContent}>
        <View style={styles.iconContainer}>{icon}</View>
        <YStack style={styles.textContainer}>
          {title && <Text style={styles.title}>{title}</Text>}
          <Text style={styles.message}>{message}</Text>
          {action && <View style={styles.actionContainer}>{action}</View>}
        </YStack>
        {isDismissable && (
          <IconButton onPress={() => setIsVisible(false)} style={styles.dismissButton}>
            <IconClose size={20} />
          </IconButton>
        )}
      </XStack>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    borderRadius: 8,
    paddingHorizontal: 16,
    paddingVertical: 12,
  },
  alertContent: {
    gap: 8,
    alignItems: 'flex-start',
  },
  iconContainer: {
    marginTop: 2,
  },
  textContainer: {
    flex: 1,
    gap: 4,
    marginLeft: 8,
  },
  title: {
    fontSize: 14,
    fontWeight: '600',
    lineHeight: 20,
  },
  message: {
    fontSize: 12,
    lineHeight: 16.8,
  },
  actionContainer: {
    marginTop: 4,
  },
  dismissButton: {
    padding: 4,
    marginLeft: 8,
  },
});
